import apiV2 from "api-v2";
import type {
	AssignRolePayload,
	FlagTogglePayload,
	Role,
	RoleTemplate,
	ScopeGrant,
	ScopeGrantInput,
	ScopeOption,
	ScopeType,
	UserAssignment,
} from "../types/RolesPermissions.type";

// Frozen RBAC surface (TASK-011 §3) lives under /api/v2/rbac/*.
const RBAC = "/rbac";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getUserAssignments = async (): Promise<UserAssignment[]> => {
	const response = await apiV2.get(`${RBAC}/users`);
	return unwrap<UserAssignment[]>(response.data) ?? [];
};

export const getRoles = async (): Promise<Role[]> => {
	const response = await apiV2.get(`${RBAC}/roles`);
	return unwrap<Role[]>(response.data) ?? [];
};

export const getRoleTemplates = async (): Promise<RoleTemplate[]> => {
	const response = await apiV2.get(`${RBAC}/templates`);
	return unwrap<RoleTemplate[]>(response.data) ?? [];
};

export const assignRole = async (
	userId: number,
	payload: AssignRolePayload
): Promise<UserAssignment> => {
	const response = await apiV2.post(`${RBAC}/users/${userId}/roles`, payload);
	return unwrap<UserAssignment>(response.data);
};

export const removeRole = async (userId: number, assignmentId: number): Promise<void> => {
	await apiV2.delete(`${RBAC}/users/${userId}/roles/${assignmentId}`);
};

export const assignTemplate = async (
	userId: number,
	templateId: number
): Promise<UserAssignment> => {
	const response = await apiV2.post(`${RBAC}/users/${userId}/template`, {
		template_id: templateId,
	});
	return unwrap<UserAssignment>(response.data);
};

// GET /rbac/scopes — the Step-2 catalogue (server-side search, capped). Identity + parent are
// server-derived; the FE passes them through verbatim and never re-derives containment.
export interface ScopeOptionsQuery {
	type?: ScopeType;
	q?: string;
	limit?: number;
}

export const getScopeOptions = async (query: ScopeOptionsQuery = {}): Promise<ScopeOption[]> => {
	const response = await apiV2.get(`${RBAC}/scopes`, { params: query });
	return unwrap<ScopeOption[]>(response.data) ?? [];
};

// C12: idempotent SET — the caller sends the full desired end-state; the server reconciles/soft-revokes.
export const setScopeGrants = async (
	userId: number,
	grants: ScopeGrantInput[]
): Promise<ScopeGrant[]> => {
	const response = await apiV2.put(`${RBAC}/users/${userId}/scopes`, { grants });
	return unwrap<ScopeGrant[]>(response.data) ?? [];
};

// C14: body is exactly {flag, enabled} — no role_id (contract drift guard). Per-USER semantics.
export const toggleUserFlag = async (
	userId: number,
	payload: FlagTogglePayload
): Promise<UserAssignment> => {
	const response = await apiV2.patch(`${RBAC}/users/${userId}/flags`, payload);
	return unwrap<UserAssignment>(response.data);
};
